<template>
  <dialog-custom
    :is-dialog-open="isDialogOpen"
    :dialog-title="dialogTitle"
    :close-dialog="isCanalNovo ? closeDialog : resetDataAndCloseDialog"
  >
    <v-form
      ref="formRef"
      class="multi-col-validation"
      @submit.prevent="handleSubmit"
    >
      <v-row>
        <v-col cols="12">
          <v-select
            v-model="tipoDeCanalSelecionado"
            dense
            outlined
            label="Tipo de canal"
            item-text="nome"
            item-value="id"
            validate-on-blur
            :loading="isGetTiposDeCanaisLoading"
            :disabled="!isCanalNovo"
            :items="tiposDeCanais"
            :rules="[validators.select]"
            @change="handleTipoDeCanalSelecionadoChange"
          />
        </v-col>
      </v-row>

      <v-expand-transition>
        <v-row v-if="isTipoDeCanalSelecionado">
          <v-col cols="12">
            <v-text-field
              v-model="canalDataLocale.nome"
              dense
              outlined
              label="Nome"
              validate-on-blur
              hint="Máximo de 50 caracteres"
              :loading="isGetCanalDataLoading"
              :rules="[validators.required, v => (!!v && v.length <= 50) || 'Máximo de 50 caracteres']"
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="canalDataLocale.descricao"
              dense
              outlined
              label="Descrição"
              validate-on-blur
              hint="Máximo de 200 caracteres"
              :loading="isGetCanalDataLoading"
              :rules="[v => (!v || v.length <= 100) || 'Máximo de 200 caracteres']"
            />
          </v-col>
        </v-row>
      </v-expand-transition>

      <v-row>
        <v-col>
          <email-config
            v-if="isCanalEmail"
            :canal-data="canalDataLocale"
            :active-tab="emailConfigActiveTab"
            :loading="isGetCanalDataLoading"
            @update:activeTab="v => emailConfigActiveTab = v"
            @update:contatoParaNotificarFalhasDeAutenticacao="v => canalDataLocale.contatoParaNotificarFalhasDeAutenticacao = v"
            @update:username="v => canalDataLocale.username = v"
            @update:password="v => canalDataLocale.password = v"
            @update:imapHost="v => canalDataLocale.imapHost = v"
            @update:imapPort="v => canalDataLocale.imapPort = v"
            @update:smtpHost="v => canalDataLocale.smtpHost = v"
            @update:smtpPort="v => canalDataLocale.smtpPort = v"
            @getMicrosoftAuthorizationCode="code => microsoftAuthorizationCode = code"
          />

          <whats-app-config
            v-else-if="isCanalWhatsApp"
            :canal-data="canalDataLocale"
            :loading="isGetCanalDataLoading"
            @update:providerId="v => canalDataLocale.providerId = v"
            @update:providerAPIToken="v => canalDataLocale.providerAPIToken = v"
            @update:providerIdentifier="v => canalDataLocale.providerIdentifier = v"
            @update:permitirAlertaTemplate="v => canalDataLocale.permitirAlertaTemplate = v"
            @update:templates="v => canalDataLocale.templates = v"
          />

          <sms-config
            v-else-if="isCanalSms"
            :canal-data="canalDataLocale"
            :loading="isGetCanalDataLoading"
            @update:providerId="v => canalDataLocale.providerId = v"
            @update:providerAPIToken="v => canalDataLocale.providerAPIToken = v"
            @update:providerIdentifier="v => canalDataLocale.providerIdentifier = v"
          />

          <meta-messenger-config
            v-else-if="isCanalMetaMessenger"
            :canal-data="canalDataLocale"
            :loading="loading"
            @update:identificadorPagina="v => canalDataLocale.identificadorPagina = v"
            @update:tokenAcessoPagina="v => canalDataLocale.tokenAcessoPagina = v"
          />

          <chat-bot-config
            v-else-if="isCanalBot"
            :canal-data="canalDataLocale"
            :loading="isGetCanalDataLoading"
            @update:timeoutRespostaSolicitanteMensagem="v => canalDataLocale.timeoutRespostaSolicitanteMensagem = v"
            @update:timeoutRespostaSolicitanteMin="v => canalDataLocale.timeoutRespostaSolicitanteMin = v"
            @update:timeoutEncerramentoAtendimentoMin="v => canalDataLocale.timeoutEncerramentoAtendimentoMin = v"
            @update:timeoutEncerramentoAtendimentoMensagem="v => canalDataLocale.timeoutEncerramentoAtendimentoMensagem = v"
          />

          <autoatendimento-config
            v-else-if="isCanalAutoatendimento"
            :canal-data="canalDataLocale"
            :loading="isGetCanalDataLoading"
            @update:dns1="v => canalDataLocale.dns1 = v"
            @update:dns2="v => canalDataLocale.dns2 = v"
            @update:tituloHeader="v => canalDataLocale.tituloHeader = v"
            @update:headerHtml="v => canalDataLocale.headerHtml = v"
            @update:footerHtml="v => canalDataLocale.footerHtml = v"
            @getLogoDataUrl="v => autoatendimentoLogoDataUrl = v"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          class="d-flex justify-center"
        >
          <v-btn
            color="primary"
            type="submit"
            class="mr-6"
          >
            <v-progress-circular
              v-if="loading"
              class="mx-6"
              indeterminate
              color="secundary"
            />
            <span v-else>{{ submitBtnTitle }}</span>
          </v-btn>

          <v-btn
            outlined
            color="secondary"
            @click="resetDataAndCloseDialog"
          >
            Cancelar
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </dialog-custom>
</template>

<script>
import Vue from 'vue'
import { ref, computed, watch, onMounted, onUnmounted } from '@vue/composition-api'
import { required, select } from '@/@core/utils/validation'
import DialogCustom from '@/components/dialog/Dialog.vue'
import EmailConfig from './email-config/EmailConfig.vue'
import WhatsAppConfig from './whatsapp-config/WhatsAppConfig.vue'
import SmsConfig from './sms-config/SmsConfig.vue'
import MetaMessengerConfig from './messenger-config/MetaMessengerConfig.vue'
import AutoatendimentoConfig from './autoatendimento-config/AutoatendimentoConfig.vue'
import ChatBotConfig from './bot-config/ChatBotConfig.vue'
import TiposDeCanaisEnum from '@/models/entities/canal/TiposDeCanaisEnum'
import CanalEmailTipoEnum from '@/models/entities/canal/CanalEmailTipoEnum'
import CanalService from '@/services/modules/CanalService'
import CanalModel from '@/models/entities/canal/CanalModel'
import CanalAberturaManualModel from '@/models/entities/canal/CanalAberturaManualModel'
import CanalEmailImapSmtpModel from '@/models/entities/canal/CanalEmailImapSmtpModel'
import CanalEmailMicrosoftModel from '@/models/entities/canal/CanalEmailMicrosoftModel'
import CanalWhatsAppModel from '@/models/entities/canal/CanalWhatsAppModel'
import CanalSmsModel from '@/models/entities/canal/CanalSmsModel'
import CanalMetaMessengerModel from '@/models/entities/canal/CanalMetaMessengerModel'
import CanalBotModel from '@/models/entities/canal/CanalBotModel'
import CanalAutoatendimentoModel from '@/models/entities/canal/CanalAutoatendimentoModel'
import useDialog from '@/composables/useDialog'

export default {
  components: {
    DialogCustom,
    EmailConfig,
    WhatsAppConfig,
    SmsConfig,
    MetaMessengerConfig,
    ChatBotConfig,
    AutoatendimentoConfig,
  },

  emits: ['update:isDialogOpen'],

  props: {
    isDialogOpen: {
      type: Boolean,
      required: true,
    },
    canalData: {
      type: Object,
      default: null,
    },
  },

  setup(props, { emit }) {
    const canalDataLocale = ref(new CanalModel())
    const isGetCanalDataLoading = ref(false)
    const tiposDeCanais = ref([])
    const tipoDeCanalSelecionado = ref(null)
    const isGetTiposDeCanaisLoading = ref(false)

    const emailConfigActiveTab = ref(0)
    const microsoftAuthorizationCode = ref(null)
    const autoatendimentoLogoDataUrl = ref(null)

    const { formRef, loading, FORM_ERROR, resetForm, validateForm, closeDialog, emitRefetchData } = useDialog(emit, 'isDialogOpen')

    function resetDataAndCloseDialog() {
      microsoftAuthorizationCode.value = null
      autoatendimentoLogoDataUrl.value = null
      resetForm()
      closeDialog()
    }

    const isCanalNovo = computed(() => !props.canalData)
    const dialogTitle = computed(() => (isCanalNovo.value ? 'Criar canal' : 'Editar canal'))
    const submitBtnTitle = computed(() => (isCanalNovo.value ? 'Criar canal' : 'Editar canal'))

    const isTipoDeCanalSelecionado = computed(() => !!tipoDeCanalSelecionado.value)
    const isCanalEmail = computed(() => tipoDeCanalSelecionado.value === TiposDeCanaisEnum.Email)
    const isCanalWhatsApp = computed(() => tipoDeCanalSelecionado.value === TiposDeCanaisEnum.WhatsApp)
    const isCanalSms = computed(() => tipoDeCanalSelecionado.value === TiposDeCanaisEnum.SMS)
    const isCanalMetaMessenger = computed(() => tipoDeCanalSelecionado.value === TiposDeCanaisEnum.MetaMessenger)
    const isCanalBot = computed(() => tipoDeCanalSelecionado.value === TiposDeCanaisEnum.Bot)
    const isCanalAutoatendimento = computed(() => tipoDeCanalSelecionado.value === TiposDeCanaisEnum.Autoatendimento)

    async function getTiposDeCanais() {
      try {
        isGetTiposDeCanaisLoading.value = true
        tiposDeCanais.value = await CanalService.getTiposDeCanais()
      } finally {
        isGetTiposDeCanaisLoading.value = false
      }
    }

    async function getCanal(canalData) {
      try {
        isGetCanalDataLoading.value = true
        tipoDeCanalSelecionado.value = canalData.canalTipoId
        const canalId = canalData.id

        switch (canalData.canalTipoId) {
          case TiposDeCanaisEnum.AberturaManual:
            canalDataLocale.value = canalData
            break
          case TiposDeCanaisEnum.Email:
          {
            canalDataLocale.value = props.canalData.canalEmailTipoId === CanalEmailTipoEnum.ImapSmtp
              ? await CanalService.getCanalEmailImapSmtp(canalId)
              : await CanalService.getCanalEmailMicrosoft(canalId)
            break
          }
          case TiposDeCanaisEnum.WhatsApp:
            canalDataLocale.value = await CanalService.getCanalWhatsApp(canalId)
            break
          case TiposDeCanaisEnum.SMS:
            canalDataLocale.value = await CanalService.getCanalSMS(canalId)
            break
          case TiposDeCanaisEnum.MetaMessenger:
            canalDataLocale.value = await CanalService.getCanalConfigMetaMessenger(canalId)
            break
          case TiposDeCanaisEnum.Bot:
            canalDataLocale.value = await CanalService.getCanalConfigChatBot(canalId)
            break
          case TiposDeCanaisEnum.Autoatendimento:
            canalDataLocale.value = await CanalService.getCanalConfigAutoatendimento(canalId)
            break
          default:
            break
        }
      } finally {
        isGetCanalDataLoading.value = false
      }
    }

    watch(
      () => props.isDialogOpen,
      v => {
        if (!v) return
        if (!props.canalData) return
        getCanal(JSON.parse(JSON.stringify(props.canalData)))
      },
      {
        immediate: true,
      },
    )
    watch(
      () => props.canalData,
      v => {
        if (!v) {
          emailConfigActiveTab.value = 0
          return
        }
        emailConfigActiveTab.value = v.canalEmailTipoId === CanalEmailTipoEnum.ImapSmtp ? 0 : 1
      },
      { immediate: true },
    )
    // ─── Listener centralizado do código OAuth Microsoft ──────────────────────
    // Fica aqui (e não em EmailMicrosoftTab) porque:
    //   1. EmailMicrosoftTab é destruído por v-if quando a aba 0 está ativa
    //   2. Há duas instâncias de SalvarCanalDialog na mesma página
    //      (CanalList = criar | TableCanais = editar); apenas a que está
    //      aberta deve processar o código e exibir o toast.
    // ──────────────────────────────────────────────────────────────────────────
    let broadcastChannel = null

    // Código recebido enquanto o dialog estava fechado (ex.: popup bloqueado +
    // redirect na aba principal → página recarrega com dialog fechado).
    const pendingMicrosoftAuthCode = ref(null)

    function applyMicrosoftAuthCode(authorizationCode) {
      microsoftAuthorizationCode.value = authorizationCode
      emailConfigActiveTab.value = 1
      Vue.$toast.info('Conta Microsoft conectada com sucesso. Agora basta salvar.')
    }

    function handleMicrosoftAuthCode(authorizationCode) {
      if (!authorizationCode) return

      if (!props.isDialogOpen) {
        // Dialog fechado nesta instância → guarda o código para quando abrir.
        // Isso cobre a instância do CanalList (dialog de criação, sempre fechado
        // enquanto o usuário edita pelo TableCanais) e o cenário de redirect.
        pendingMicrosoftAuthCode.value = authorizationCode
        return
      }

      applyMicrosoftAuthCode(authorizationCode)
    }

    // Quando o dialog abre, processa qualquer código pendente (cenário de redirect)
    watch(
      () => props.isDialogOpen,
      isOpen => {
        if (isOpen && pendingMicrosoftAuthCode.value) {
          const code = pendingMicrosoftAuthCode.value
          pendingMicrosoftAuthCode.value = null
          applyMicrosoftAuthCode(code)
        }
      },
    )

    function onStorageEvent(event) {
      if (event.key === 'microsoft-auth-code' && event.newValue) {
        localStorage.removeItem('microsoft-auth-code')
        handleMicrosoftAuthCode(event.newValue)
      }
    }

    function onPostMessage(event) {
      if (event.origin !== window.location.origin) return
      if (event.data?.type === 'microsoft-auth-code') {
        handleMicrosoftAuthCode(event.data.authorizationCode)
      }
    }

    onMounted(() => {
      getTiposDeCanais()

      // BroadcastChannel (principal — funciona entre popup e qualquer aba/janela)
      try {
        broadcastChannel = new BroadcastChannel('microsoft-oauth')
        broadcastChannel.onmessage = event => {
          if (event.data?.type === 'microsoft-auth-code') {
            handleMicrosoftAuthCode(event.data.authorizationCode)
          }
        }
      } catch (_) { /* browser sem suporte */ }

      // Storage event (fallback — popup → main window via localStorage)
      window.addEventListener('storage', onStorageEvent)

      // postMessage (fallback legado — popup com window.opener disponível)
      window.addEventListener('message', onPostMessage)

      // Checa localStorage imediatamente (cobre reload com code pendente de redirect)
      const pendingCode = localStorage.getItem('microsoft-auth-code')
      if (pendingCode) {
        localStorage.removeItem('microsoft-auth-code')
        handleMicrosoftAuthCode(pendingCode) // pode guardar em pendingMicrosoftAuthCode se dialog fechado
      }
    })

    onUnmounted(() => {
      broadcastChannel?.close()
      window.removeEventListener('storage', onStorageEvent)
      window.removeEventListener('message', onPostMessage)
    })

    function handleTipoDeCanalSelecionadoChange(novoTipoDeCanal) {
      switch (novoTipoDeCanal) {
        case TiposDeCanaisEnum.AberturaManual:
          canalDataLocale.value = new CanalAberturaManualModel()
          break
        case TiposDeCanaisEnum.Email: {
          canalDataLocale.value = emailConfigActiveTab.value === 0
            ? new CanalEmailImapSmtpModel()
            : new CanalEmailMicrosoftModel()
          break
        }
        case TiposDeCanaisEnum.WhatsApp:
          canalDataLocale.value = new CanalWhatsAppModel()
          break
        case TiposDeCanaisEnum.SMS:
          canalDataLocale.value = new CanalSmsModel()
          break
        case TiposDeCanaisEnum.MetaMessenger:
          canalDataLocale.value = new CanalMetaMessengerModel()
          break
        case TiposDeCanaisEnum.Bot:
          canalDataLocale.value = new CanalBotModel()
          break
        case TiposDeCanaisEnum.Autoatendimento:
          canalDataLocale.value = new CanalAutoatendimentoModel()
          break
        default:
          break
      }
    }

    function getCanalEmailRequestBody() {
      const request = {
        nome: canalDataLocale.value.nome,
        descricao: canalDataLocale.value.descricao,
        contatoParaNotificarFalhasDeAutenticacao: canalDataLocale.value.contatoParaNotificarFalhasDeAutenticacao,
      }

      if (emailConfigActiveTab.value === 0) {
        return {
          ...request,
          imapSmtpConfig: {
            username: canalDataLocale.value.username,
            password: canalDataLocale.value.password,
            imapPort: canalDataLocale.value.imapPort,
            imapHost: canalDataLocale.value.imapHost,
            smtpPort: canalDataLocale.value.smtpPort,
            smtpHost: canalDataLocale.value.smtpHost,
          },
        }
      }

      // Aba Microsoft — envia authorizationCode null se não trocou conta
      return {
        ...request,
        microsoftConfig: {
          authorizationCode: microsoftAuthorizationCode.value ?? null, // 👈 null = mantém tokens do banco
        },
      }
    }
    function getCanalAutoatendimentoRequestBody() {
      return {
        ...canalDataLocale.value,
        logoDataUrl: autoatendimentoLogoDataUrl.value,
      }
    }

    async function criarCanal() {
      switch (tipoDeCanalSelecionado.value) {
        case TiposDeCanaisEnum.AberturaManual:
          return CanalService.createCanalAberturaManual(canalDataLocale.value)
        case TiposDeCanaisEnum.Email:
          return CanalService.createCanalEmail(getCanalEmailRequestBody())
        case TiposDeCanaisEnum.WhatsApp: {
          const response = await CanalService.createCanalWhatsApp(canalDataLocale.value)
          const canalId = response?.data?.data?.id
          const templates = canalDataLocale.value.templates ?? []
          for (const template of templates) {
            await CanalService.createTemplateWhatsApp(canalId, template)
          }
          return response
        }
        case TiposDeCanaisEnum.SMS:
          return CanalService.createCanalSms(canalDataLocale.value)
        case TiposDeCanaisEnum.MetaMessenger:
          return CanalService.createCanalMetaMessenger(canalDataLocale.value)
        case TiposDeCanaisEnum.Bot:
          return CanalService.createCanalChatBot(canalDataLocale.value)
        case TiposDeCanaisEnum.Autoatendimento:
          return CanalService.createCanalAutoatendimento(getCanalAutoatendimentoRequestBody())
        default:
      }
    }

    async function updateCanal() {
      const canalId = canalDataLocale.value.id

      switch (tipoDeCanalSelecionado.value) {
        case TiposDeCanaisEnum.AberturaManual:
          return CanalService.updateCanalAberturaManual(canalId, canalDataLocale.value)
        case TiposDeCanaisEnum.Email:
          return CanalService.updateCanalEmail(canalId, getCanalEmailRequestBody())
        case TiposDeCanaisEnum.WhatsApp:
          return CanalService.updateCanalWhatsApp(canalId, canalDataLocale.value)
        case TiposDeCanaisEnum.SMS:
          return CanalService.updateCanalSms(canalId, canalDataLocale.value)
        case TiposDeCanaisEnum.MetaMessenger:
          return CanalService.updateCanalMetaMessenger(canalId, canalDataLocale.value)
        case TiposDeCanaisEnum.Bot:
          return CanalService.updateCanalChatBot(canalId, canalDataLocale.value)
        case TiposDeCanaisEnum.Autoatendimento:
          return CanalService.updateCanalAutoatendimento(canalId, getCanalAutoatendimentoRequestBody())
        default:
      }
    }

    async function handleSubmit() {
      if (loading.value) return
      if (!validateForm()) {
        Vue.$toast(FORM_ERROR)
        return
      }

      try {
        loading.value = true

        if (isCanalNovo.value) {
          await criarCanal()
          resetDataAndCloseDialog()
        } else {
          await updateCanal()
          closeDialog()
        }

        emitRefetchData()
      } finally {
        loading.value = false
      }
    }

    return {
      canalDataLocale,
      isGetCanalDataLoading,
      tiposDeCanais,
      tipoDeCanalSelecionado,
      isGetTiposDeCanaisLoading,

      emailConfigActiveTab,
      microsoftAuthorizationCode,
      autoatendimentoLogoDataUrl,

      formRef,
      loading,
      closeDialog,
      resetDataAndCloseDialog,

      isCanalNovo,
      dialogTitle,
      submitBtnTitle,

      isTipoDeCanalSelecionado,
      isCanalEmail,
      isCanalWhatsApp,
      isCanalSms,
      isCanalAutoatendimento,
      isCanalMetaMessenger,
      isCanalBot,

      handleTipoDeCanalSelecionadoChange,
      handleSubmit,

      validators: {
        select,
        required,
      },
    }
  },
}
</script>
